if (!customElements.get('case-scale-product')) {
  class CaseScaleProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('[data-case-scale-form]');
      if (!this.form) return;
      this.copy = JSON.parse(this.querySelector('[data-case-scale-copy]').textContent);
      this.quantity = this.querySelector('[data-case-scale-quantity]');
      this.buttons = [...this.querySelectorAll('[data-case-scale-submit]')];
      this.button = this.buttons[0];
      if (this.dataset.directCheckout === 'true') {
        this.copy.add = this.button.textContent;
        this.copy.add_many = this.copy.add;
      }
      this.options = [...this.querySelectorAll('[data-case-scale-option]')];
      this.summaries = [...this.querySelectorAll('[data-case-scale-summary]')];
      this.error = this.querySelector('[data-case-scale-error]');
      this.status = this.querySelector('[data-case-scale-status]');
      this.recovery = this.querySelector('[data-case-scale-recovery]');
      this.money = new Intl.NumberFormat(this.dataset.locale, {style: 'currency', currency: this.dataset.currency});
      this.options.forEach(option => {option.hidden = false;});
      this.summaries.forEach(summary => {summary.hidden = false;});
      const combined = this.querySelector('[data-case-scale-combined]');
      if (combined && this.options.length) combined.hidden = false;
      this.sticky = this.querySelector('[data-case-scale-sticky]');
      this.stickyTotal = this.querySelector('[data-case-scale-sticky-total]');
      this.stickyLabel = this.querySelector('[data-case-scale-sticky-label]');
      this.purchaseBlock = this.querySelector('.case-scale__purchase');
      this.purchaseVisible = true;
      this.pastFold = false;
      const view = this.ownerDocument?.defaultView;
      if (this.sticky && view) {
        const Observer = view.IntersectionObserver;
        if (Observer && this.purchaseBlock) {
          new Observer(entries => {
            this.purchaseVisible = entries[0].isIntersecting;
            this.syncSticky();
          }).observe(this.purchaseBlock);
        }
        const onScroll = () => {
          this.pastFold = (view.scrollY || 0) > 400;
          this.syncSticky();
        };
        view.addEventListener('scroll', onScroll, {passive: true});
        onScroll();
      }
      this.addEventListener('input', () => this.render());
      this.addEventListener('change', () => this.render());
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.addCase();
      });
      this.render();
    }
    item(source, input) {
      const quantity = Number(input.value);
      const min = Number(input.min || 1);
      const step = Number(input.step || 1);
      const max = input.hasAttribute('max') ? Number(input.max) : Infinity;
      const id = Number(source.dataset.variant);
      const price = Number(source.dataset.price);
      const packs = Number(source.dataset.packs);
      if (!Number.isSafeInteger(quantity) || !Number.isSafeInteger(min) || min < 1 || !Number.isSafeInteger(step) || step < 1 || quantity < min || quantity > max || (quantity - min) % step !== 0 || !Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(price) || price < 0 || !Number.isSafeInteger(packs) || packs <= 0 || !Number.isSafeInteger(price * quantity) || !Number.isSafeInteger(packs * quantity)) return null;
      return {id, quantity, price, packs, name: source.dataset.name};
    }
    selection() {
      const main = this.item(this, this.quantity);
      if (!main) return null;
      const wanted = [main];
      for (const option of this.options) {
        if (!option.querySelector('[data-case-scale-include]').checked) continue;
        const extra = this.item(option, option.querySelector('[data-case-scale-extra-quantity]'));
        if (!extra || wanted.some(item => item.id === extra.id)) return null;
        wanted.push(extra);
      }
      const subtotal = wanted.reduce((total, item) => total + item.price * item.quantity, 0);
      const packs = wanted.reduce((total, item) => total + item.packs * item.quantity, 0);
      return Number.isSafeInteger(subtotal) && Number.isSafeInteger(packs) ? wanted : null;
    }
    interpolate(template, values) {
      return template.replace(/__(\w+)__/g, (_, key) => String(values[key] ?? ''));
    }
    renderSummary(wanted) {
      this.summaries.forEach(summary => {
        summary.replaceChildren();
        if (!wanted) {summary.textContent = this.copy.summary_invalid; return;}
        const list = document.createElement('ul');
        for (const item of wanted) {
          const row = document.createElement('li');
          row.textContent = this.interpolate(this.copy.summary_line, {quantity: item.quantity, product: item.name, packs: item.packs * item.quantity, price: this.money.format(item.price * item.quantity / 100)});
          list.append(row);
        }
        const total = document.createElement('p');
        total.className = 'case-scale__selection-total';
        const cases = wanted.reduce((n, item) => n + item.quantity, 0);
        total.textContent = this.interpolate(cases === 1 ? this.copy.summary_total_one : this.copy.summary_total, {cases, packs: wanted.reduce((n, item) => n + item.packs * item.quantity, 0), price: this.money.format(wanted.reduce((n, item) => n + item.price * item.quantity, 0) / 100)});
        summary.append(list, total);
      });
    }
    syncSticky() {
      if (!this.sticky) return;
      const locked = Boolean(this.busy || this.uncertain || this.completed);
      this.sticky.hidden = locked || this.purchaseVisible || !this.pastFold;
    }
    render() {
      const locked = Boolean(this.busy || this.uncertain || this.completed);
      const wanted = this.selection();
      const addCopy = wanted?.length > 1 ? this.copy.add_many : this.copy.add;
      this.buttons.forEach(button => {
        button.disabled = locked;
        button.textContent = this.completed ? this.copy.added : this.busy ? this.copy.busy : addCopy;
      });
      this.quantity.disabled = locked;
      this.options.forEach(option => {
        const checkbox = option.querySelector('[data-case-scale-include]');
        const input = option.querySelector('[data-case-scale-extra-quantity]');
        checkbox.disabled = locked;
        input.disabled = locked || !checkbox.checked;
        input.closest('label').hidden = !checkbox.checked;
        option.classList.toggle('is-selected', checkbox.checked);
      });
      this.renderSummary(wanted);
      if (this.stickyTotal && wanted) {
        this.stickyTotal.textContent = this.money.format(wanted.reduce((n, item) => n + item.price * item.quantity, 0) / 100);
      }
      if (this.stickyLabel && wanted) {
        const packs = wanted.reduce((n, item) => n + item.packs * item.quantity, 0);
        this.stickyLabel.textContent = `${packs} ${this.stickyLabel.dataset.packs}${wanted.length === 1 ? ` · ${wanted[0].name}` : ''}`;
      }
      this.syncSticky();
    }
    async readCart() {
      const response = await fetch(`${this.dataset.root}cart.js`, {credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'application/json'}});
      if (!response.ok) throw new Error('cart_read_failed');
      const cart = await response.json();
      if (!Array.isArray(cart.items) || cart.currency !== this.dataset.currency || !Number.isInteger(cart.item_count) || cart.item_count < 0 || cart.items.some(item => !Number.isSafeInteger(item.quantity) || item.quantity < 1 || !Number.isSafeInteger(Number(item.variant_id)) || Number(item.variant_id) <= 0 || typeof item.key !== 'string' || !item.key) || cart.items.reduce((n, item) => n + item.quantity, 0) !== cart.item_count) throw new Error('cart_shape_invalid');
      return cart;
    }
    preservesExisting(cart, before) {
      return before.items.every(old => cart.items.some(item => item.key === old.key && Number(item.variant_id) === Number(old.variant_id) && item.quantity >= old.quantity && (item.selling_plan_allocation?.selling_plan?.id ?? null) === (old.selling_plan_allocation?.selling_plan?.id ?? null)));
    }
    unchanged(cart, before) {
      return cart.items.length === before.items.length && cart.item_count === before.item_count && this.preservesExisting(cart, before);
    }
    verified(cart, wanted, marker, before) {
      if (!wanted || cart.currency !== this.dataset.currency || !(cart.item_count > 0) || !Array.isArray(cart.items)) return false;
      const added = cart.items.filter(item => item.properties?._case_scale_selection === marker);
      if (added.length !== wanted.length || new Set(added.map(item => Number(item.variant_id))).size !== wanted.length) return false;
      if (!wanted.every(expected => added.some(actual => Number(actual.variant_id) === expected.id && actual.quantity === expected.quantity && !actual.selling_plan_allocation && Number.isSafeInteger(actual.final_price) && actual.final_price >= 0 && actual.final_price <= expected.price))) return false;
      return this.preservesExisting(cart, before);
    }
    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
      this.error.focus();
    }
    accept(cart, wanted, marker, before) {
      if (!this.verified(cart, wanted, marker, before)) return false;
      this.completed = true;
      this.uncertain = false;
      this.status.textContent = this.copy.added;
      this.status.hidden = false;
      this.recovery.hidden = false;
      this.recovery.focus();
      if (this.dataset.directCheckout === 'true') {
        // The cart is verified before navigating. Keep the checkout link usable
        // if navigation is unavailable, without submitting the selection again.
        try {window.location.assign(this.recovery.href);} catch (_) {}
      }
      return true;
    }
    async addCase() {
      if (this.busy || this.uncertain || this.completed) return;
      const wanted = this.selection();
      if (!wanted) {this.showError(this.copy.invalid); return;}
      this.busy = true;
      this.error.hidden = true;
      this.status.hidden = true;
      this.recovery.hidden = true;
      this.render();
      let sent = false;
      let before;
      let marker;
      try {
        before = await this.readCart();
        marker = crypto.randomUUID();
        sent = true;
        const response = await fetch(`${this.dataset.root}cart/add.js`, {
          method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({items: wanted.map(item => ({id: item.id, quantity: item.quantity, properties: {_case_scale_selection: marker, _case_scale_campaign: 'case-scale-20261002'}}))})
        });
        const cart = await this.readCart();
        if (this.accept(cart, wanted, marker, before)) return;
        const rejectedCleanly = response.status >= 400 && response.status < 500 && !cart.items.some(item => item.properties?._case_scale_selection === marker) && this.unchanged(cart, before);
        this.uncertain = !rejectedCleanly;
        this.recovery.hidden = !this.uncertain;
        this.showError(rejectedCleanly ? this.copy.rejected : this.copy.uncertain);
      } catch (_) {
        this.uncertain = sent;
        if (sent && before && marker) {
          try {if (this.accept(await this.readCart(), wanted, marker, before)) return;} catch (_) {this.uncertain = true;}
        }
        this.recovery.hidden = !this.uncertain;
        this.showError(sent ? this.copy.uncertain : this.copy.read_error);
      } finally {
        this.busy = false;
        this.render();
      }
    }
  }
  customElements.define('case-scale-product', CaseScaleProduct);
}
